import React, { useEffect, useState } from 'react';

import { useAppConfig } from '@context/AppConfigContext.tsx';
import { useSDKHandler } from '@features/method-tester/hooks';

import { useFetchRequest } from '@/hooks';
import fetchRequest from '@/utils';
import { Button } from '@asphalt-react/button';
import { Card } from '@asphalt-react/card';
import { Loader } from '@asphalt-react/loader';
import { Numeric } from '@asphalt-react/textfield';
import { OpenGopayDeeplinkResult } from '@gopay/miniapp-client';

type SnapCheckoutTokenResponse = {
  token: string;
};

type SnapCheckoutTokenRequest = {
  transaction_details: {
    gross_amount: number;
  };
};

const fetchSnapCheckoutToken = async (
  baseURL: string,
  body: SnapCheckoutTokenRequest
) => {
  return await fetchRequest<SnapCheckoutTokenResponse>(
    `${baseURL}/v1/miniverse/payment/snap-checkout`,
    {
      method: 'POST',
      body,
    }
  );
};

type PaymentDeepLinkResponse = {
  name: string;
  method: string;
  url: string;
};

type PaymentDeepLinkRequest = {
  payment_type: 'gopay';
  transaction_details: {
    gross_amount: number;
    currency: 'IDR';
  };
};

const fetchPaymentDeepLink = async (
  baseURL: string,
  body: PaymentDeepLinkRequest
) => {
  return await fetchRequest<PaymentDeepLinkResponse>(
    `${baseURL}/v1/miniverse/payment/deeplink`,
    {
      method: 'POST',
      body,
    }
  );
};
export const PaymentIntegration = () => {
  const [amount, setAmount] = useState<number>(100);
  const { VITE_BASE_API_URL: baseURL } = useAppConfig();

  const {
    data: deepLinkData,
    error: deepLinkError,
    request: requestDeepLink,
    loading: deepLinkFetchLoading,
  } = useFetchRequest<PaymentDeepLinkResponse>();

  const {
    isLoading: deepLinkTriggerLoading,
    response: deepLinkTriggerResp,
    invokeSDKActionV1,
  } = useSDKHandler<OpenGopayDeeplinkResult>();

  const {
    data: snapCheckoutTokenData,
    error: snapCheckoutTokenError,
    request: requestSnapCheckoutToken,
    loading: snapCheckoutTokenLoading,
  } = useFetchRequest<SnapCheckoutTokenResponse>();

  const [snapCheckoutCallbackResponse, setSnapCheckoutCallbackResponse] =
    useState<unknown | null>(null);

  useEffect(() => {
    setSnapCheckoutCallbackResponse(null);
  }, [amount]);

  useEffect(() => {
    const launchDeepLink = async () => {
      await triggerDeepLink();
    };
    if (deepLinkData?.url) {
      launchDeepLink();
    }
  }, [deepLinkData]);

  const onAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const amount = Number(value);
    setAmount(amount);
  };

  const getPaymentDeepLink = async () => {
    const body: PaymentDeepLinkRequest = {
      payment_type: 'gopay',
      transaction_details: {
        gross_amount: amount,
        currency: 'IDR',
      },
    };

    await requestDeepLink(() => fetchPaymentDeepLink(baseURL, body));
  };

  const triggerDeepLink = async () => {
    try {
      await invokeSDKActionV1('GP', 'launchPayment', {
        deeplink: deepLinkData?.url,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const initiatePayment = async () => {
    setSnapCheckoutCallbackResponse(null);
    await getPaymentDeepLink();
  };

  const initiateSnapCheckout = async () => {
    setSnapCheckoutCallbackResponse(null);
    window.snap?.show?.();
    const body: SnapCheckoutTokenRequest = {
      transaction_details: {
        gross_amount: amount,
      },
    };
    await requestSnapCheckoutToken(() => fetchSnapCheckoutToken(baseURL, body));
  };

  useEffect(() => {
    if (snapCheckoutTokenError) {
      window.snap?.hide?.();
    } else if (snapCheckoutTokenData?.token) {
      window.snap?.pay?.(snapCheckoutTokenData.token, {
        onSuccess: (result) => {
          console.log('payment success!', result);
          setSnapCheckoutCallbackResponse(result);
        },
        onPending: (result) => {
          console.log('wating your payment!', result);
          setSnapCheckoutCallbackResponse(result);
        },
        onError: (result) => {
          console.log('payment failed!', result);
          setSnapCheckoutCallbackResponse(result);
        },
        onClose: () => {
          alert('you closed the popup without finishing the payment');
        },
      });
    }
  }, [snapCheckoutTokenData, snapCheckoutTokenError]);

  return (
    <Card>
      <div>
        <div className="pt-2 flex flex-col gap-2">
          <label className="pr-2">Amount:</label>
          <Numeric
            value={amount}
            onChange={onAmountChange}
            stretch={false}
            size="xs"
          />

          {(deepLinkFetchLoading ||
            deepLinkTriggerLoading ||
            snapCheckoutTokenLoading) && <Loader></Loader>}

          <Button size="xs" onClick={initiatePayment}>
            Seamless Payment
          </Button>
          {deepLinkError && (
            <div className="mt-2 w-full">
              <div
                data-testid="payment-integration-error"
                className="overflow-auto text-sm bg-gray-100 p-2 rounded"
              >
                {JSON.stringify(deepLinkError, null, 2)}
              </div>
            </div>
          )}
          {deepLinkTriggerResp && (
            <div className="mt-2 w-full">
              <pre
                data-testid="payment-integration-response"
                className="overflow-auto text-sm bg-gray-100 p-2 rounded"
              >
                {JSON.stringify(deepLinkTriggerResp, null, 2)}
              </pre>
            </div>
          )}

          <Button size="xs" onClick={initiateSnapCheckout}>
            Snap Pay (main-frame WITH its own JSSDK)
          </Button>
          {snapCheckoutTokenError && (
            <div className="mt-2 w-full">
              <div
                data-testid="payment-integration-error"
                className="overflow-auto text-sm bg-gray-100 p-2 rounded"
              >
                {JSON.stringify(snapCheckoutTokenError, null, 2)}
              </div>
            </div>
          )}
          {snapCheckoutTokenData && (
            <div className="mt-2 w-full">
              <pre
                data-testid="payment-integration-response"
                className="overflow-auto text-sm bg-gray-100 p-2 rounded"
              >
                {JSON.stringify(snapCheckoutTokenData, null, 2)}
              </pre>
            </div>
          )}
          {!!snapCheckoutCallbackResponse && (
            <div className="mt-2 w-full">
              <pre
                data-testid="payment-integration-response"
                className="overflow-auto text-sm bg-gray-100 p-2 rounded"
              >
                {JSON.stringify(snapCheckoutCallbackResponse, null, 2)}
              </pre>
            </div>
          )}

          <Button
            size="xs"
            secondary
            onClick={() => {
              window.location.href = '/snap.html';
            }}
          >
            Open Snap Pay Page (main-frame WITHOUT its own JSSDK)
          </Button>
        </div>
      </div>
    </Card>
  );
};
